/**
 * The timeout contract for `useAuthedFetch`.
 *
 * Deliberately in its own dependency-free module: `useAuthedFetch` transitively imports
 * `appsConfig`, which pulls in image assets, so anything defined there cannot be
 * imported by a plain node test runner. An error type that the UI branches on should be
 * testable without standing up the whole app graph.
 */

/**
 * Thrown when a request exceeded its opt-in `timeoutMs`.
 *
 * Distinct from a generic network failure so callers can say "this is taking too long"
 * rather than "something went wrong", and can offer a retry that means something.
 */
export class RequestTimeoutError extends Error {
  readonly path: string;
  readonly timeoutMs: number;

  constructor(path: string, timeoutMs: number) {
    super(`Request to ${path} timed out after ${timeoutMs}ms`);
    this.name = "RequestTimeoutError";
    this.path = path;
    this.timeoutMs = timeoutMs;
  }
}

export function isRequestTimeoutError(e: unknown): e is RequestTimeoutError {
  return e instanceof RequestTimeoutError;
}

/**
 * Rejects as soon as `signal` aborts, even if `promise` never settles.
 *
 * An `AbortSignal` only cancels operations that accept one. `fetch` does; the Clerk
 * token mint and the device-id lookup do NOT — so awaiting them directly meant a
 * request deadline protected the network call while the two steps BEFORE it could hang
 * indefinitely. Checking `aborted` after the await cannot help: the await is what never
 * returns.
 *
 * The underlying promise is NOT cancelled — nothing here can cancel it — it is merely
 * abandoned. That is the honest limit of this helper: it bounds how long a CALLER
 * waits, not how long the work runs.
 */
export function raceAbort<T>(
  promise: Promise<T>,
  signal: AbortSignal,
  makeError: () => Error
): Promise<T> {
  if (signal.aborted) return Promise.reject(makeError());

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(makeError());
    signal.addEventListener("abort", onAbort, { once: true });

    const settle = <A>(handler: (value: A) => void) => (value: A) => {
      signal.removeEventListener("abort", onAbort);
      handler(value);
    };

    promise.then(settle(resolve), settle(reject));
  });
}
